import { useMemo, useState } from 'react';
import { EffectivePermission, GrantRule, ResourceEntry } from '../lib/types';
import { actionLabel } from '../lib/format';
import { Empty } from './ui';

type Effect = 'allow' | 'deny' | null;

const WILDCARD = '*';
const ACTION_ORDER = ['read', 'create', 'update', 'delete'];

function key(resource: string, action: string): string {
  return `${resource} ${action}`;
}

/**
 * The permission matrix. A cell has three states and clicking cycles through
 * them in order:
 *
 *   empty     no rule - does not count as permission
 *   tick      allowed
 *   cross     explicitly denied; beats every allow on the same layer
 *
 * The row "All resources" writes a wildcard rule. That is the difference
 * between two rows in the database and nine hundred.
 *
 * With that many cells the grid has to answer "which row and which column am I
 * in" without being asked, so the resource column stays put while the actions
 * scroll and the pointer lights its own row and column.
 */
export function PermissionMatrix({
  resources,
  rules,
  onChange,
  effective,
  readOnly,
}: {
  resources: ResourceEntry[];
  rules: GrantRule[];
  onChange: (rules: GrantRule[]) => void;
  effective?: EffectivePermission[];
  readOnly?: boolean;
}) {
  const [filter, setFilter] = useState('');
  const [onlyGranted, setOnlyGranted] = useState(false);
  const [hover, setHover] = useState<{ resource: string; action: string } | null>(null);

  const current = useMemo(() => {
    const map = new Map<string, Effect>();
    for (const rule of rules) map.set(key(rule.resource, rule.action), rule.effect);
    return map;
  }, [rules]);

  const effectiveMap = useMemo(() => {
    const map = new Map<string, EffectivePermission>();
    for (const entry of effective ?? []) map.set(key(entry.resource, entry.action), entry);
    return map;
  }, [effective]);

  const actions = useMemo(() => {
    const found = new Set<string>();
    for (const resource of resources) resource.actions.forEach((action) => found.add(action));
    return [
      ...ACTION_ORDER.filter((action) => found.has(action)),
      ...[...found].filter((action) => !ACTION_ORDER.includes(action)).sort(),
    ];
  }, [resources]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return resources.filter((resource) => {
      if (needle && !resource.resource.toLowerCase().includes(needle)) return false;
      if (onlyGranted) {
        return resource.actions.some((action) => current.get(key(resource.resource, action)));
      }
      return true;
    });
  }, [resources, filter, onlyGranted, current]);

  function setEffect(resource: string, action: string, effect: Effect): void {
    const next = rules.filter((rule) => !(rule.resource === resource && rule.action === action));
    if (effect) next.push({ resource, action, effect });
    onChange(next);
  }

  function cycle(resource: string, action: string): void {
    if (readOnly) return;
    const state = current.get(key(resource, action)) ?? null;
    setEffect(resource, action, state === null ? 'allow' : state === 'allow' ? 'deny' : null);
  }

  function allowAllReads(): void {
    const next = rules.filter((rule) => !(rule.resource === WILDCARD && rule.action === 'read'));
    next.push({ resource: WILDCARD, action: 'read', effect: 'allow' });
    onChange(next);
  }

  if (!resources.length) {
    return (
      <Empty>
        No specification is active for this module yet - so there are no permissions to grant.
      </Empty>
    );
  }

  const columns = [...actions, WILDCARD];

  return (
    <>
      <div className="matrix-toolbar">
        <input
          type="search"
          placeholder="Filter resource"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <label className="checkbox" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={onlyGranted}
            onChange={(event) => setOnlyGranted(event.target.checked)}
          />
          <span>granted only</span>
        </label>
        <span className="muted tiny">
          {visible.length} of {resources.length} resources
        </span>
        {readOnly ? null : (
          <div className="row" style={{ marginLeft: 'auto' }}>
            <button className="small" onClick={allowAllReads}>
              Allow all reads
            </button>
            <button className="small" onClick={() => onChange([])}>
              Reset
            </button>
          </div>
        )}
      </div>

      <div className="matrix-scroll" onMouseLeave={() => setHover(null)}>
        <table className="matrix">
          <thead>
            <tr>
              <th className="resource-head">Resource</th>
              {columns.map((action) => (
                <th
                  key={action}
                  className={hover?.action === action ? 'action col-hot' : 'action'}
                >
                  {action === WILDCARD ? 'All' : actionLabel(action)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className={hover?.resource === WILDCARD ? 'wildcard row-hot' : 'wildcard'}>
              <td className="resource">
                All resources <span className="mono muted">*</span>
              </td>
              {columns.map((action) => (
                <Cell
                  key={action}
                  state={current.get(key(WILDCARD, action)) ?? null}
                  hot={hover?.action === action}
                  onClick={() => cycle(WILDCARD, action)}
                  onHover={() => setHover({ resource: WILDCARD, action })}
                  readOnly={readOnly}
                />
              ))}
            </tr>

            {visible.map((resource) => (
              <tr
                key={resource.resource}
                className={hover?.resource === resource.resource ? 'row-hot' : undefined}
              >
                <td className="resource">
                  {resource.resource}
                  {resource.writeActions.length ? null : <span className="muted"> - read only</span>}
                </td>
                {columns.map((action) => {
                  const supported = action === WILDCARD || resource.actions.includes(action);
                  const state = current.get(key(resource.resource, action)) ?? null;
                  const inherited =
                    state === null && isCoveredByWildcard(current, resource.resource, action);
                  const blocked = effectiveMap.get(key(resource.resource, action));
                  return supported ? (
                    <Cell
                      key={action}
                      state={state}
                      inherited={inherited}
                      hot={hover?.action === action}
                      blockedBy={blocked && !blocked.allowed ? blocked.blockedBy : undefined}
                      onClick={() => cycle(resource.resource, action)}
                      onHover={() => setHover({ resource: resource.resource, action })}
                      readOnly={readOnly}
                    />
                  ) : (
                    <td
                      key={action}
                      className={hover?.action === action ? 'cell dash col-hot' : 'cell dash'}
                      onMouseEnter={() => setHover({ resource: resource.resource, action })}
                    >
                      &ndash;
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Every state has its own glyph as well as its own colour - the grid has
          to stay readable for someone who cannot tell the green from the red. */}
      <div className="matrix-legend">
        <span className="key">
          <span className="glyph allow">✓</span> allowed
        </span>
        <span className="key">
          <span className="glyph deny">✕</span> denied — beats every allow on this layer
        </span>
        <span className="key">
          <span className="glyph inherited">✓</span> allowed via a wildcard rule
        </span>
        <span className="key">
          <span className="glyph empty">·</span> no rule
        </span>
        {readOnly ? null : <span className="key faint">Click a cell to cycle.</span>}
      </div>
    </>
  );
}

function isCoveredByWildcard(current: Map<string, Effect>, resource: string, action: string): boolean {
  return (
    current.get(key(WILDCARD, action)) === 'allow' ||
    current.get(key(resource, WILDCARD)) === 'allow' ||
    current.get(key(WILDCARD, WILDCARD)) === 'allow'
  );
}

function Cell({
  state,
  inherited,
  hot,
  blockedBy,
  onClick,
  onHover,
  readOnly,
}: {
  state: Effect;
  inherited?: boolean;
  hot?: boolean;
  blockedBy?: string;
  onClick: () => void;
  onHover: () => void;
  readOnly?: boolean;
}) {
  const tone = state === 'allow' ? 'allow' : state === 'deny' ? 'deny' : inherited ? 'inherited' : 'empty';
  const symbol = state === 'allow' ? '✓' : state === 'deny' ? '✕' : inherited ? '✓' : '·';

  const title = blockedBy
    ? `Has no effect: blocked on the "${blockedBy}" layer`
    : inherited
      ? 'Allowed via a wildcard rule'
      : undefined;

  return (
    <td className={hot ? 'cell col-hot' : 'cell'} title={title} onMouseEnter={onHover}>
      <button
        type="button"
        className={`cell-button ${tone}${blockedBy ? ' blocked' : ''}`}
        onClick={onClick}
        disabled={readOnly}
        style={{ cursor: readOnly ? 'default' : 'pointer' }}
      >
        {symbol}
      </button>
    </td>
  );
}
